import React, { useCallback, useContext, useMemo } from 'react';
import CommonConstants from 'sg-common-constants';
import SphConstants from 'sg-sph-constants';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { SgSessionStorageService, SgProductService, SgWebchatUtil } from 'saga-modules';

import { Link } from '@jutro/components';
import messages from './SgSphError.messages';

function SgSphError(props) {
    const {
        errorCodeT,
        errorCodeC
    } = props;

    const {
        SG_ERROR_CODES,
        SG_ERROR_REASONS,
        SAGA_PHONE_NUMBERS,
        SAGA_HOME
    } = CommonConstants;

    const {
        SG_HOME_ERROR_REASONS
    } = SphConstants;

    const trackChat = useCallback((event) => {
        if (event) {
            event.preventDefault();
        }
        SgWebchatUtil.trackChat();
    }, []);

    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';

    const headerContent = useMemo(() => {
        // for T0 and T1 for home
        return translator(messages.ErrorHeader);
    }, [errorCodeT]);

    const returnToHomepageButtonContent = useMemo(() => {
        return (
            <div>
                <Link href={SAGA_HOME} className="sgErrorPageNewQuoteLink">
                    {translator(messages.SagaHomepage)}
                </Link>
            </div>
        );
    }, []);

    const beginNewQuoteContent = useMemo(() => {
        if (SgProductService.isQuickQuoteHome() && errorCodeC !== SG_ERROR_REASONS.PRICING_OR_ARCHIE_ERROR) {
            return null;
        }
        let newQuoteHref = '/journey';
        if (SgProductService.isQuickQuoteHome()) {
            const id = SgSessionStorageService.getItem(SgSessionStorageService.KEY.QQ_BASE_ID);
            newQuoteHref = `/new-quickQuote?id=${id}&product=${CommonConstants.HOME_PRODUCT_CODE}`;
        }
        return (
            <div>
                <Link to={newQuoteHref} className="sgErrorPageNewQuoteLink">
                    {translator(messages.NewQuote)}
                </Link>
            </div>
        );
    }, []);

    const getDeclineMessageContent = useCallback(() => {
        switch (errorCodeC) {
            case SG_HOME_ERROR_REASONS.PROPERTY_DIVIDED_INTO_BEDSITS: // T0 C9144
            case SG_ERROR_REASONS.PRICING_OR_ARCHIE_ERROR: // T0 C9124
            case SG_HOME_ERROR_REASONS.DECLINED_BUSINESS_USE_ASSUMPTIONS: // T0 C9155
            case SG_HOME_ERROR_REASONS.SWITCHED_FROM_TAILORMADE_TO_STANDARD: // T0 C9149
                return (
                    <>
                        <p className="sgErrorPagePrimaryText">{translator(messages.HomeErrorText1)}</p>
                        {beginNewQuoteContent}
                        {returnToHomepageButtonContent}
                    </>
                );
            case SG_HOME_ERROR_REASONS.POLICY_CANCELLED_NON_PAYMENT: // T0 C9113
            case SG_HOME_ERROR_REASONS.CUSTOMER_IS_BANKRUPT_OR_HASCCJ: // T0 C9152
                return (
                    <>
                        <p className="sgErrorPagePrimaryText">{translator(messages.HomeErrorText2)}</p>
                        {returnToHomepageButtonContent}
                    </>
                );
            case SG_HOME_ERROR_REASONS.CUSTOMER_UNDER_50: // T0 C9117
                return (
                    <>
                        <p className="sgErrorPagePrimaryText">{translator(messages.HomeErrorAge)}</p>
                        {beginNewQuoteContent}
                        {returnToHomepageButtonContent}
                    </>
                );
            case SG_HOME_ERROR_REASONS.LANDLORD_3_OR_MORE_WITH_SAGA: // T0 C9154
                return (
                    <>
                        <p className="sgErrorPagePrimaryText">
                            {translator(messages.HomeErrorThreeProperties, {
                                phoneNumber: isMobile
                                    ? (
                                        <a
                                            className="sgErrorPagePrimaryText"
                                            href={`tel:${SAGA_PHONE_NUMBERS.SAGA_HOME_ERROR_THREE_PROPERTIES_PHONE_NUMBER.replaceAll(' ', '')}`}
                                        >
                                            {SAGA_PHONE_NUMBERS.SAGA_HOME_ERROR_THREE_PROPERTIES_PHONE_NUMBER}
                                        </a>
                                    ) : SAGA_PHONE_NUMBERS.SAGA_HOME_ERROR_THREE_PROPERTIES_PHONE_NUMBER
                            })}
                        </p>
                        {returnToHomepageButtonContent}
                    </>
                );
            case SG_HOME_ERROR_REASONS.NO_LANDLORD_TENANCY_AGREEMENT_OVER_6_MONTHS: // T0 C9156
                return (
                    <>
                        <p className="sgErrorPagePrimaryText">
                            {translator(messages.HomeErrorTenancyAgreement, {
                                phoneNumber: isMobile
                                    ? (
                                        <a
                                            className="sgErrorPagePrimaryText"
                                            href={`tel:${SAGA_PHONE_NUMBERS.SAGA_HOME_LANDLORD_PHONE_NUMBER.replaceAll(' ', '')}`}
                                        >
                                            {SAGA_PHONE_NUMBERS.SAGA_HOME_LANDLORD_PHONE_NUMBER}
                                        </a>
                                    ) : SAGA_PHONE_NUMBERS.SAGA_HOME_LANDLORD_PHONE_NUMBER
                            })}
                        </p>
                        {returnToHomepageButtonContent}
                    </>
                );
            case SG_HOME_ERROR_REASONS.FLOOD_CLAIM: // T0 C9129
                return (
                    <>
                        <p className="sgErrorPagePrimaryText">{translator(messages.HomeErrorFlood)}</p>
                        {returnToHomepageButtonContent}
                    </>
                );
            case SG_ERROR_REASONS.ABILITY_TO_PAY_ERROR: // T0 C9199
                return (
                    <>
                        <p className="sgErrorPagePrimaryText">
                            {translator(messages.HomeErrorErrorMessageAbilityToPay,
                                { phoneNumber: SAGA_PHONE_NUMBERS.SAGA_HELP_DESK_HOME_PHONE_NUMBER })}
                        </p>
                        {returnToHomepageButtonContent}
                    </>
                );
            default:
                return (
                    <>
                        <p className="sgErrorPagePrimaryText">{translator(messages.HomeErrorText2)}</p>
                        {beginNewQuoteContent}
                        {returnToHomepageButtonContent}
                    </>
                );
        }
    }, [errorCodeC, translator, isMobile]);

    const getReferralMessageContent = useCallback(() => {
        switch (errorCodeC) {
            case SG_HOME_ERROR_REASONS.INELIGIBLE_BUSINESS_USE_TYPE: // T1 C9143
                return (
                    <>
                        <p className="sgErrorPagePrimaryText">{translator(messages.HomeErrorReferralBusinessType1)}</p>
                        <p className="sgErrorPageSecondaryText">
                            {translator(messages.HomeErrorReferralBusinessType2, {
                                phoneNumber: isMobile
                                    ? (
                                        <a href={`tel:${SAGA_PHONE_NUMBERS.SAGA_HOME_ERROR_PAGE_PHONE_NUMBER1.replaceAll(' ', '')}`}>
                                            {SAGA_PHONE_NUMBERS.SAGA_HOME_ERROR_PAGE_PHONE_NUMBER1}
                                        </a>
                                    ) : SAGA_PHONE_NUMBERS.SAGA_HOME_ERROR_PAGE_PHONE_NUMBER1
                            })}
                        </p>
                        {beginNewQuoteContent}
                        {returnToHomepageButtonContent}
                    </>
                );
            case SG_HOME_ERROR_REASONS.NO_LANDLORD_TENANCY_AGREEMENT_OVER_6_MONTHS: // T1 C9156
            case SG_HOME_ERROR_REASONS.CONTENTS_VALUE_LESS_THAN_SEVENTY_FIVE_THOUSAND_AND_BUILDINGS_COVER_OVER_MILLION: // T1 C 9147
            case SG_HOME_ERROR_REASONS.POLICY_CANCELLED_NON_PAYMENT: // T1 C9113
                return (
                    <>
                        <p className="sgErrorPagePrimaryText">
                            {translator(messages.HomeErrorReferralPleaseCall, {
                                phoneNumber: isMobile
                                    ? (
                                        <a
                                            className="sgErrorPagePrimaryText"
                                            href={`tel:${SAGA_PHONE_NUMBERS.SAGA_HOME_ERROR_PAGE_PHONE_NUMBER2.replaceAll(' ', '')}`}
                                        >
                                            {SAGA_PHONE_NUMBERS.SAGA_HOME_ERROR_PAGE_PHONE_NUMBER2}
                                        </a>
                                    ) : SAGA_PHONE_NUMBERS.SAGA_HOME_ERROR_PAGE_PHONE_NUMBER2
                            })}
                        </p>
                        {beginNewQuoteContent}
                        {returnToHomepageButtonContent}
                    </>
                );
            case SG_HOME_ERROR_REASONS.FLOODING_AT_THE_PROPERTY: // T1 C9160
                return (
                    <>
                        <p className="sgErrorPagePrimaryText">
                            {translator(messages.HomeErrorReferralFlooding, {
                                phoneNumber: isMobile
                                    ? (
                                        <a
                                            className="sgErrorPagePrimaryText"
                                            href={`tel:${SAGA_PHONE_NUMBERS.SAGA_HOME_ERROR_PAGE_PHONE_NUMBER1.replaceAll(' ', '')}`}
                                        >
                                            {SAGA_PHONE_NUMBERS.SAGA_HOME_ERROR_PAGE_PHONE_NUMBER1}
                                        </a>
                                    ) : SAGA_PHONE_NUMBERS.SAGA_HOME_ERROR_PAGE_PHONE_NUMBER1
                            })}
                        </p>
                        {beginNewQuoteContent}
                        {returnToHomepageButtonContent}
                    </>
                );
            default:
                return (
                    <>
                        <p className="sgErrorPagePrimaryText">
                            {translator(messages.HomeErrorReferralGeneral1)}
                            <Link
                                href="/"
                                id="webchatlink"
                                onClick={trackChat}
                                className="saga-intercom"
                            >
                                {translator(messages.WebChat)}
                            </Link>
                            {translator(messages.HomeErrorReferralGeneral2)}
                        </p>
                        {beginNewQuoteContent}
                        {returnToHomepageButtonContent}
                    </>
                );
        }
    }, [errorCodeC, translator, isMobile]);

    const messageContent = useMemo(() => {
        switch (errorCodeT) {
            case SG_ERROR_CODES.SAGA_DECLINE: // T0
            case SG_ERROR_CODES.SAGA_POLARIS_REFERRAL: // T12
                return getDeclineMessageContent();
            case SG_ERROR_CODES.SAGA_REFERRAL: // T1
                return getReferralMessageContent();
            default:
                return '';
        }
    }, [errorCodeT, errorCodeC, isMobile]);

    return (
        <div>
            <h1>{headerContent}</h1>
            <div>{messageContent}</div>
        </div>
    );
}

export default SgSphError;
